import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInputUtils from "intl-tel-input/build/js/utils";

export function initPhoneInput(selector, {
    initialCountry = "fr",
    preferredCountries = ["fr", "be", "ch"],
    onlyCountries = ["al","ad","at","by","be","ba","bg","hr","cz","dk",
        "ee","fo","fi","fr","de","gi","gr","va","hu","is",
        "ie","it","lv","li","lt","lu","mk","mt","md","mc",
        "me","nl","no","pl","pt","ro","ru","sm","rs","sk",
        "si","es","se","ch","ua","gb"],
    nationalMode = false,
    countrySearch = false,
    validationNumberTypes = ['FIXED_LINE', 'MOBILE'],
    onChangeNumber = () => {},
    onChangeCountry = () => {},
    onChangeValidity = () => {},
    onChangeErrorCode = () => {},
    initialValue = ""
} = {}) {
    const input = typeof selector === "string" ? document.querySelector(selector) : selector;
    if (!input) return null;

    const iti = intlTelInput(input, {
        initialCountry,
        preferredCountries,
        nationalMode,
        countrySearch,
        onlyCountries,
        validationNumberTypes,
        loadUtils: () => import("intl-tel-input/build/js/utils")
    });

    let wasPreviouslyValid = false;

    function isValid() {

        const pluginValid = iti.isValidNumberPrecise(); // on force la validation stricte : Même comportement que libphonenumber côté Laravel
        if (!pluginValid) return false;

        const formatted = iti.getNumber();
        const country = iti.getSelectedCountryData()?.iso2;

        if (country === "fr") {
            // Vérifier que le numéro national (sans indicatif) ne commence pas par 0
            const digits = formatted.replace(/\D/g, "");
            const dialCode = iti.getSelectedCountryData()?.dialCode || "33";
            const national = digits.startsWith(dialCode) ? digits.slice(dialCode.length) : digits;
            if (national.startsWith("0")) return false;
        }
        if (isFakePattern(formatted, iti.getSelectedCountryData()?.dialCode)) return false;

        return true;
    }

    function isFakePattern(num, dialCode = "") {
        // Extraire uniquement les chiffres
        let digits = (num || "").replace(/\D/g, "");

        // Supprimer l'indicatif international si connu
        if (dialCode) {
            if (digits.startsWith(dialCode)) {
                digits = digits.slice(dialCode.length);
            }
        }

        // Si après retrait, trop court → on ne valide pas
        if (digits.length < 6) return false;

        // 1. Tous les chiffres identiques
        if (/^(\d)\1+$/.test(digits)) return true;

        // 2. Séquence croissante simple
        if ("0123456789".includes(digits)) return true;

        // 3. Séquence décroissante simple
        if ("9876543210".includes(digits)) return true;

        // 4. Répétition de motifs (ex: 123123123)
        if (digits.length % 3 === 0) {
            const part = digits.substring(0, digits.length / 3);
            if (digits === part.repeat(3)) return true;
        }

        // 5. Combinaisons évidentes (ex: 000111222, 444555666)
        if (/^(\d{3})(\d{3})(\d{3})$/.test(digits)) {

            const [, a, b, c] = digits.match(/^(\d{3})(\d{3})(\d{3})$/);
            // Si blocs identiques OU blocs qui forment une suite croissante simple
            const blocks = [a, b, c].map(x => parseInt(x, 10));
            const isSequential = blocks[1] === blocks[0] + 111 && blocks[2] === blocks[1] + 111;

            if (a === b || b === c || a === c || isSequential) return true;
        }

        return false;
    }


    function updateValidity() {
        const currentValid = isValid();
        const errorCode = currentValid ? null : iti.getValidationError();

        if (wasPreviouslyValid !== currentValid) {
            wasPreviouslyValid = currentValid;
            onChangeValidity(currentValid);
        }
        onChangeErrorCode(errorCode);
    }

    function onChange() {
        const rawInput = input.value;
        const formatted = iti.getNumber(intlTelInputUtils.numberFormat.INTERNATIONAL) || "";
        input.value = formatted;
        onChangeNumber(formatted);

        updateValidity();

        // Reformate seulement si valide
        if (isValid() && rawInput !== formatted) {
            iti.setNumber(formatted);
        }
    }

    function onCountryChange() {
        const country = iti?.getSelectedCountryData().iso2 ?? "";
        onChangeCountry(country);
        onChange();
    }

    input.addEventListener("input", () => {
        const formatted = iti.getNumber(intlTelInputUtils.numberFormat.INTERNATIONAL) || "";
        input.value = formatted;
        onChangeNumber(formatted);
        updateValidity();
    });

    // Sur changement de pays → callback
    input.addEventListener("countrychange", onCountryChange);

    // Sur blur → on reformate proprement
    input.addEventListener("blur", () => {
        const formatted = iti.getNumber(intlTelInputUtils.numberFormat.INTERNATIONAL) || "";
        if (formatted) iti.setNumber(formatted);
    });

    // Si l'input a déjà une valeur → formatage initial
    iti.promise.then( () => {
        if (initialValue) {
            iti.setNumber(initialValue);
        }
        updateValidity();
    })


    // Exposer les méthodes utiles
    return {
        iti,
        isValid,
        getNumber: (format = intlTelInputUtils.numberFormat.INTERNATIONAL) => iti.getNumber(format),
        setNumber: (num) => iti.setNumber(num),
        destroy: () => iti.destroy()
    };
}
